/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Managing users and groups", description = "Hightouch xxxx", lastUpdatedDate = new Date(1733702400000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Managing users and groups",
        description: "Hightouch xxxx",
        lastUpdatedDate: "Mon Dec 09 2024 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Users",
          "depth": 0,
          "id": "users"
        }, {
          "value": "Inviting users",
          "depth": 1,
          "id": "inviting-users"
        }, {
          "value": "Removing users",
          "depth": 1,
          "id": "removing-users"
        }, {
          "value": "Groups",
          "depth": 0,
          "id": "groups"
        }, {
          "value": "Assigning users to groups",
          "depth": 1,
          "id": "assigning-users-to-groups"
        }, {
          "value": "Creating a new group",
          "depth": 1,
          "id": "creating-a-new-group"
        }, {
          "value": "Granting a group access to a workspace",
          "depth": 1,
          "id": "granting-a-group-access-to-a-workspace"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      p: "p",
      a: "a",
      h2: "h2",
      strong: "strong",
      img: "img",
      h3: "h3",
      ul: "ul",
      li: "li"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.p, {
        children: "Before you begin, it's essential to understand the structure of a Hightouch organization. Access control is exclusively managed at the group level, so configuring users and groups should be your first step during the onboarding process."
      }), "\n", _jsxs(_components.p, {
        children: ["This guide provides an overview of common user management tasks, such as ", _jsx(_components.a, {
          href: "#inviting-users",
          children: "inviting users"
        }), ", ", _jsx(_components.a, {
          href: "#assigning-users-to-groups",
          children: "assigning them to groups"
        }), ", and ", _jsx(_components.a, {
          href: "#granting-a-group-access-to-a-workspace",
          children: "managing group access"
        }), " to workspaces. Once you've set up users and groups, you can proceed to ", _jsx(_components.a, {
          href: "/workspace-management/roles",
          children: "define custom roles"
        }), " and manage more granular permissions."]
      }), "\n", _jsx(Tip, {
        children: _jsxs(_components.p, {
          children: ["If you're planning to connect Hightouch to your company's identity provider\n(e.g., Okta), it's recommended to review the ", _jsx(_components.a, {
            href: "/workspace-management/sso",
            children: "single sign-on (SSO)\ndocumentation"
          }), " before inviting other users to your\norganization. An identity provider can automate many of the following through\nSAML SSO and SCIM."]
        })
      }), "\n", _jsx(_components.h2, {
        id: "users",
        children: "Users"
      }), "\n", _jsxs(_components.p, {
        children: ["To view a list of users in your organization, navigate to the ", _jsx(_components.strong, {
          children: "Organization settings"
        }), " page and select the ", _jsx(_components.strong, {
          children: "Users"
        }), " tab. There, you'll find a table of all users in your organization, including their authentication methods and group memberships. You can click on any user in the table to reveal more detailed information."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "workspaces/permissions/all-users-in-org.png",
          alt: "List of all users in an org"
        })
      }), "\n", _jsx(_components.h3, {
        id: "inviting-users",
        children: "Inviting users"
      }), "\n", _jsxs(_components.p, {
        children: ["If your organization is configured to use SSO, there's no need to manually invite users. Instead, go to ", _jsx(_components.strong, {
          children: "Organization settings"
        }), " > ", _jsx(_components.strong, {
          children: "Single sign-on"
        }), " and copy the ", _jsx(_components.strong, {
          children: "Hightouch login URL"
        }), ". Share this link with your teammates so they can log in through your identity provider—no invite required."]
      }), "\n", _jsxs(_components.p, {
        children: ["For organizations not using SSO, you can invite others by going to ", _jsx(_components.strong, {
          children: "Organization settings"
        }), " > ", _jsx(_components.strong, {
          children: "Users"
        }), " and clicking the ", _jsx(_components.strong, {
          children: "Invite user"
        }), " button. Only members of the ", _jsx(_components.strong, {
          children: "Organization admins"
        }), " group can send invites. Make sure to assign the recipient to at least one user group so they receive workspace access upon their first login."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "workspaces/permissions/invite-user.png",
          alt: "Inviting a new user to the org"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["A list of pending invitations is available at the top of ", _jsx(_components.strong, {
          children: "Organization settings"
        }), " > ", _jsx(_components.strong, {
          children: "Users"
        }), ", where you can also withdraw invites if necessary. Otherwise, invites will expire automatically after 30 days."]
      }), "\n", _jsx(_components.h3, {
        id: "removing-users",
        children: "Removing users"
      }), "\n", _jsxs(_components.p, {
        children: ["Go to ", _jsx(_components.strong, {
          children: "Organization settings"
        }), " > ", _jsx(_components.strong, {
          children: "Users"
        }), ", select the user you wish to remove, and click ", _jsx(_components.strong, {
          children: "Remove user"
        }), "."]
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "Removing a user doesn't prevent them from rejoining your organization in the\nfuture if they receive another invite or authenticate through your central\nidentity provider."
        })
      }), "\n", _jsx(_components.h2, {
        id: "groups",
        children: "Groups"
      }), "\n", _jsxs(_components.p, {
        children: ["A ", _jsx(_components.strong, {
          children: "group"
        }), " is a collection of users. In Hightouch, access control is managed at the group level, making it easy to update permissions for many users at once."]
      }), "\n", _jsx(_components.p, {
        children: "Every Hightouch organization includes two built-in groups that cannot be deleted:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Organization admins"
          }), " have full access to all workspaces and can manage organization-wide settings such as users, groups, billing, SSO, and more."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Organization viewers"
          }), " have read-only access to all workspaces, but they do not have any admin privileges whatsoever."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Each organization must always have at least one admin, while the viewers group is optional and exists purely for convenience."
      }), "\n", _jsx(_components.h3, {
        id: "assigning-users-to-groups",
        children: "Assigning users to groups"
      }), "\n", _jsx(_components.p, {
        children: "User-to-group mappings are best managed by your identity provider (e.g., Okta). However, if SSO group mappings aren't enabled or you need custom groups, you can manually set up user groups and assign users directly within the Hightouch app."
      }), "\n", _jsxs(_components.p, {
        children: ["To manage group assignments for a single user, navigate to ", _jsx(_components.strong, {
          children: "Organization settings"
        }), " > ", _jsx(_components.strong, {
          children: "Users"
        }), ", select a user from the list, and use the ", _jsx(_components.strong, {
          children: "Assign"
        }), " toggle for each group to add or remove the user."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "workspaces/permissions/add-user-to-groups.png",
          alt: "Add a user to one or more groups"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["To manage multiple members in a group, go to ", _jsx(_components.strong, {
          children: "Organization settings"
        }), " > ", _jsx(_components.strong, {
          children: "Groups"
        }), ", choose the group from the sidebar, and select the ", _jsx(_components.strong, {
          children: "Members"
        }), " tab."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "workspaces/permissions/group-members.png",
          alt: "List of all users in a group"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["To add more users, click ", _jsx(_components.strong, {
          children: "Add users"
        }), "; to remove existing users, check the boxes next to their names and click ", _jsx(_components.strong, {
          children: "Remove users"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "workspaces/permissions/add-users-to-group.png",
          alt: "Add multiple users to a group"
        })
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "If group memberships are controlled by your identity provider via SSO group\nmappings, you cannot override or make subtractive changes (e.g., removing\nusers from groups). However, you can make additive changes, such as assigning\nadditional members to an existing group."
        })
      }), "\n", _jsx(_components.h3, {
        id: "creating-a-new-group",
        children: "Creating a new group"
      }), "\n", _jsx(_components.p, {
        children: "Each group should represent a set of users with similar access to workspaces and their associated resources. For instance, you might create one group for “Data Science” and another for “Growth Marketing”. Groups can also be used to reflect different teams, regions, brands, or any other organizational structure that impacts permissions."
      }), "\n", _jsx(_components.p, {
        children: "To simplify your onboarding, it's recommended to keep the number of groups to a minimum. Remember, users can belong to multiple groups at the same time, inheriting combined permissions from all their assigned groups. This can help reduce the total number of groups needed in your organization. For example, instead of creating a separate group for “Growth Marketing Analysts”, you might instead assign those users to both the “Data Science” and “Growth Marketing” groups."
      }), "\n", _jsxs(_components.p, {
        children: ["To create a new group, go to ", _jsx(_components.strong, {
          children: "Organization settings"
        }), " > ", _jsx(_components.strong, {
          children: "Groups"
        }), " and click ", _jsx(_components.strong, {
          children: "Add group"
        }), ". Name the group and select the members to include."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "workspaces/permissions/new-group.png",
          alt: "Creating a new group"
        })
      }), "\n", _jsx(_components.h3, {
        id: "granting-a-group-access-to-a-workspace",
        children: "Granting a group access to a workspace"
      }), "\n", _jsxs(_components.p, {
        children: ["By default, new groups don't have access to any workspaces. To grant access, navigate to ", _jsx(_components.strong, {
          children: "Organization settings"
        }), " > ", _jsx(_components.strong, {
          children: "Groups"
        }), ", select the group from the sidebar, and open the ", _jsx(_components.strong, {
          children: "Workspaces"
        }), " tab. Use the ", _jsx(_components.strong, {
          children: "Role"
        }), " dropdown to assign the appropriate access level for each workspace."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "workspaces/permissions/group-workspaces.png",
          alt: "Workspace access for a group"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Roles determine the actions a group can perform within a workspace. You can select from built-in roles (e.g., “Workspace viewer”) or create a custom role for more fine-tuned control. For more information, refer to the ", _jsx(_components.a, {
          href: "/workspace-management/roles",
          children: "documentation for roles"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.strong, {
          children: "Access"
        }), " column provides an overview of the group's permissions for each workspace, showing the actions they can take for each destination. While it gives a helpful summary, it doesn't display every possible grant or cover permissions related to other resource types."]
      })]
    });
  }
}
export default MDXContent;
